import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isNotifyPath } from "./paths";

/** public/notify/sw.js asks every open app window where it is (reply `{path}`
 * on the port the message carries) when a notification is tapped. */
export const WHERE_MESSAGE = "notify:where";
/** public/notify/sw.js tells the window that answered with a Notify route to
 * route to `url` (the tapped case). */
export const OPEN_MESSAGE = "notify:open";

/** The in-app path to route to for a service worker message, or null when the
 * message is not ours or its URL is not a same-origin Notify page. */
export function openTarget(data: unknown, origin = window.location.origin): string | null {
  if (!data || typeof data !== "object") return null;
  const { type, url } = data as { type?: unknown; url?: unknown };
  if (type !== OPEN_MESSAGE || typeof url !== "string") return null;
  let target: URL;
  try {
    target = new URL(url, origin);
  } catch {
    return null;
  }
  if (target.origin !== origin || !isNotifyPath(target.pathname)) return null;
  return target.pathname + target.search + target.hash;
}

const typeOf = (data: unknown) =>
  data && typeof data === "object" ? (data as { type?: unknown }).type : undefined;

/**
 * The page side of a notification tap, mounted once for the whole app
 * (App.tsx). The worker can't see which route a window shows (its Client.url
 * stays the URL it was opened at), so it asks (`notify:where`) and this answers
 * with the current path. Only a window on a Notify route is then told to open
 * the case (`notify:open`), and the page checks it again on arrival: a page that
 * isn't on a Notify route then (it moved in between, or a worker built before
 * this check sent it) ignores it, so a web-demo page is never moved. Only a same-origin Notify
 * URL is followed. It keeps `?via=push`, so the case page records the
 * receipt as after any other tap.
 */
export function useNotifyOpen() {
  const navigate = useNavigate();
  useEffect(() => {
    const container = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!container) return;
    const onMessage = (event: MessageEvent) => {
      // Messages from our own worker carry our origin (an empty one is allowed:
      // some browsers leave it unset for worker messages).
      if (event.origin && event.origin !== window.location.origin) return;
      if (typeOf(event.data) === WHERE_MESSAGE) {
        event.ports[0]?.postMessage({ path: window.location.pathname });
        return;
      }
      // Only a page that is on Notify now is moved: it may have left since it
      // answered notify:where, and a worker built before this check (only local
      // and draft builds ever had one) sends notify:open without asking.
      if (!isNotifyPath(window.location.pathname)) return;
      const path = openTarget(event.data);
      if (path) navigate(path);
    };
    container.addEventListener("message", onMessage);
    // Delivers messages queued before the listener (a no-op once the page loaded).
    container.startMessages?.();
    return () => container.removeEventListener("message", onMessage);
  }, [navigate]);
}
